/**
 * Typed clients for router-side documentation endpoints (T07 / T10).
 *
 * These endpoints live on the **router** origin, not on the hub:
 *   - GET /v1/models                — re-exported from `lib/openai-client.ts`
 *                                     so we have one source of truth for the
 *                                     shared model shape and base-URL resolver.
 *   - GET /api/pricing              — added in T10 for the Docs page.
 *   - GET /api/models/capabilities  — added in T10 for the Docs page.
 *
 * Auth: not required for any of these read endpoints — they're public
 * discovery data. The router exposes CORS, so the SPA fetches directly
 * without a hub proxy.
 *
 * Base URL: resolved via `routerBaseUrl()` from `lib/openai-client.ts` to
 * keep one resolver for `VITE_ROUTER_URL`.
 *
 * `PUBLIC_PROXY_BASE` is intentionally **decoupled** from the runtime base.
 * It is the canonical public URL shown to users in code samples and the
 * "Base URL" card — never used as a fetch target.
 */

import { listModels, routerBaseUrl, type OpenAIModel } from '../lib/openai-client';

/** Canonical public proxy URL shown in code samples and the "Base URL" card. */
export const PUBLIC_PROXY_BASE = 'https://api.opengonka.com/v1';

/**
 * Re-export of the OpenAI-compatible model shape from `lib/openai-client.ts`
 * under the Docs-page-friendly name. Single source of truth — adding a field
 * to `OpenAIModel` propagates here automatically.
 */
export type ModelEntry = OpenAIModel;

/**
 * Re-export of `listModels()` from `lib/openai-client.ts` under the
 * Docs-page-friendly name. Returns the `data[]` array directly (envelope
 * already unwrapped).
 */
export const fetchModels = listModels;

// ---------------------------------------------------------------------------
// Internal JSON fetch helper.
// ---------------------------------------------------------------------------

async function fetchJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const url = `${routerBaseUrl()}${path}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!res.ok) {
    // Surface only the HTTP status; upstream body may contain detail we don't
    // want rendered verbatim in the UI (security-reviewer T07 NB note).
    throw new Error(`GET ${path} failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// /api/pricing
// ---------------------------------------------------------------------------

export interface PricingRow {
  model: string;
  per_token_ngonka: string | null;
  per_token_usd: number | null;
  per_1m_usd: number | null;
  display_name?: string;
}

export interface PricingResponse {
  models: PricingRow[];
  gnk_usd_rate: number | null;
  markup_pct: number | null;
  refreshed_at: string;
  source: string;
}

export async function fetchPricing(signal?: AbortSignal): Promise<PricingResponse> {
  return fetchJson<PricingResponse>('/api/pricing', signal);
}

// ---------------------------------------------------------------------------
// /api/models/capabilities
// ---------------------------------------------------------------------------

export interface CapabilityEntry {
  id: string;
  hf_repo: string;
  hf_commit: string;
  context_window: number | null;
  tools_supported: boolean;
  reasoning_supported: boolean;
  tool_parser: string | null;
  reasoning_parser: string | null;
  vram_gb: number | null;
  compute_per_token: number | null;
  validation_threshold_pct: number | null;
  proposed_by: string | null;
}

export interface CapabilitiesResponse {
  models: CapabilityEntry[];
  refreshed_at: string;
  source: string;
}

export async function fetchCapabilities(signal?: AbortSignal): Promise<CapabilitiesResponse> {
  return fetchJson<CapabilitiesResponse>('/api/models/capabilities', signal);
}
